
import {toRefs, reactive, defineComponent, PropType, computed} from 'vue'
import DealDTO from "@/models/DealDTO";
import {paymentMethodsHelper} from "@/service/PaymentMethodsHelper"
import {numberFormatter} from "@/service/NumberFormatter"
import InvoiceStateDTO from "@/models/InvoiceStateDTO";
import copyTextToClipboard from "@/service/CopyHelper";
const apiUrl = window.API_URL;

export default defineComponent({
  props: {
    deal: {
      type: Object as PropType<DealDTO>,
      default: false
    },
    invoice: {
      type: Object as PropType<InvoiceStateDTO>,
      default: false
    }
  },
  setup(props) {

    const state = reactive({
      copyHintTimeout: null as number|null,
      copyValue: '' as string,
    });

    const qrCodeUrl = computed(() => `${apiUrl}/api/public/invoices/${props.deal.id}/qr-code`)
    const qrCodePngUrl = computed(() => `${apiUrl}/api/public/invoices/${props.deal.id}/qr-code-png`)
    const showVietQrMobileSaveFlow = computed(() => {
      return props.deal.paymentOption === 'VIET_QR'
        && props.invoice.flowData?.options?.vietQrMobileSaveFlow === true
    })
    const vietQrBanks = computed(() => {
      return (props.invoice.flowData?.options?.vietQrBanks ?? []) as string[]
    })
    const brQrBanks = computed(() => {
      return (props.invoice.flowData?.options?.brQrBanks ?? []) as string[]
    })
    const vietQrVndWarning = computed(() => {
      return props.invoice.flowData?.vietQrVndWarning ?? null
    })
    const orderSumText = computed(() => {
      return `${numberFormatter.formatNumber(Number(props.invoice.sum.amount), 2)} ${props.invoice.sum.currency}`
    })
    const vndTransferSumText = computed(() => {
      const transferSum = vietQrVndWarning.value?.transferSum
      if (!transferSum) {
        return null
      }
      return `${numberFormatter.formatNumber(Number(transferSum.amount), 0)} ${transferSum.currency}`
    })
    const vndWarningOrderSum = computed(() => {
      return vndTransferSumText.value ? orderSumText.value : `${numberFormatter.formatNumber(4865, 2)} RUB`
    })
    const vndWarningTransferSum = computed(() => {
      return vndTransferSumText.value ?? `${numberFormatter.formatNumber(1497508, 0)} VND`
    })
    const saveQrLabel = computed(() => {
      if (props.deal.paymentOption === 'IND_QR') {
        return 'invoice.ind-qr-instruction.save-qr'
      }
      if (props.deal.paymentOption === 'BR_QR') {
        return 'invoice.br-qr-instruction.save-qr'
      }
      return 'invoice.viet-qr-instruction.save-qr'
    })

    const downloadQr = async () => {
      let filePrefix = 'viet-qr'
      if (props.deal.paymentOption === 'IND_QR') {
        filePrefix = 'qris'
      } else if (props.deal.paymentOption === 'BR_QR') {
        filePrefix = 'pix'
      }
      const fileName = `${filePrefix}-${props.deal.id}.png`
      let blob: Blob

      try {
        const response = await fetch(qrCodePngUrl.value)
        blob = await response.blob()
      } catch (e) {
        window.open(qrCodePngUrl.value, '_blank')
        return
      }

      const isIOS = /iPhone|iPod|iPad/i.test(navigator.userAgent)
      if (isIOS && typeof navigator.canShare === 'function') {
        const file = new File([blob], fileName, {type: blob.type || 'image/png'})
        if (navigator.canShare({files: [file]})) {
          try {
            await navigator.share({files: [file]})
          } catch (e) {
          }
          return
        }
      }

      try {
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
      } catch (e) {
        window.open(qrCodePngUrl.value, '_blank')
      }
    }

    const vndAmount = computed(() => {
      if (props.deal.paymentOption !== 'VIET_QR') {
        return null
      }

      const amount = Math.round(Number(props.deal.requisites?.displayAmountVND))

      return Number.isFinite(amount) && amount > 0 ? amount : null
    })
    const vndAmountFormatted = computed(() => {
      return vndAmount.value !== null ? numberFormatter.formatMoney(vndAmount.value, 'VND') : null
    })

    const copy = (obj: any, value: string) => {
      state.copyValue = value
      copyTextToClipboard(obj, value)
      if (state.copyHintTimeout) {
        clearTimeout(state.copyHintTimeout)
      }
      state.copyHintTimeout = setTimeout(() => {
        state.copyHintTimeout = null
      }, 1000)
    }

    return {
      ...toRefs(state),
      paymentMethodsHelper,
      numberFormatter,
      apiUrl,
      qrCodeUrl,
      showVietQrMobileSaveFlow,
      vietQrBanks,
      brQrBanks,
      vietQrVndWarning,
      orderSumText,
      vndTransferSumText,
      vndWarningOrderSum,
      vndWarningTransferSum,
      saveQrLabel,
      qrCodePngUrl,
      downloadQr,
      vndAmount,
      vndAmountFormatted,
      copy
    }
  }
})
